<template>
  <Radar :data="radarChartData" :darkmode="darkmode" />
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import Radar from "@/components/charts/Radar.vue";
import { useCampaign } from "@/queries/campaigns.query";
import { SkillInfo } from "@/stores/skills";
import { Participant } from "@/types/participant";
import { Scores } from "@/types/results";

const route = useRoute();
const { t } = useI18n();

const appStore = useAppStore();

const { data: campaign, isPending } = useCampaign(appStore.onboardingIsLive);

const skillsStore = useSkillsStore();

const props = defineProps<{
  assessedSkills: SkillInfo[],
  participant: Participant,
  averageScores: Scores,
  darkmode: boolean,
}>();

const talentScores = computed(() => {
  if (!props.participant.results) return [];

  const sortedResults = Object.entries(props.participant.results.scores)
    .filter(([key, value]) => {
      if (value === null || value === undefined) {
        return false;
      }

      if (key.startsWith("leadership") || key.startsWith('social_desirability')) return false;
      const elt = skillsStore.skills.find((skill) => skill.id === key);
      if (!elt || elt.disabled || elt.incompatible_versions.includes(campaign.value.campaign_version)) return false;
      return true;
    }
    ).sort(([id_a], [id_b]) => {
      const formattedIdA = skillsStore.getFormattedSkillId(id_a);
      const formattedIdB = skillsStore.getFormattedSkillId(id_b);
      return formattedIdA.localeCompare(formattedIdB);
    });

  const res = sortedResults.map(([key, result]: [string, number]) => {
    return Math.round(result * 100);
  });
  return res;
});

const campaignAverageScore = computed(() => {
  if (!props.averageScores) return [];

  const sortedResults = Object.entries(props.averageScores)
    .filter(([key, value]) => {
      if (value === null || value === undefined) {
        return false;
      }

      if (key.startsWith("leadership") || key.startsWith('social_desirability')) return false;
      const elt = skillsStore.skills.find((skill) => skill.id === key);
      if (!elt || elt.disabled || elt.incompatible_versions.includes(campaign.value.campaign_version)) return false;
      return true;
    }
    ).sort(([id_a], [id_b]) => {
      const formattedIdA = skillsStore.getFormattedSkillId(id_a);
      const formattedIdB = skillsStore.getFormattedSkillId(id_b);
      return formattedIdA.localeCompare(formattedIdB);
    });

  const res = sortedResults.map(([key, result]) => {
    return Math.round(result * 100);
  });
  return res;
})

const radarChartData = computed(() => {
  return {
    labels: props.assessedSkills.sort((a: any, b: any) => {
      const formattedIdA = skillsStore.getFormattedSkillId(a.id);
      const formattedIdB = skillsStore.getFormattedSkillId(b.id);
      return formattedIdA.localeCompare(formattedIdB);
    }).map((elt: any) => t(`skills.${elt.id}.name`)),
    datasets: [
      {
        label: `${props.participant?.candidate?.first_name} ${props.participant?.candidate?.last_name}`,
        backgroundColor: "rgba(242,203,54,0.2)",
        borderColor: "rgba(242,203,54,1)",
        pointBackgroundColor: "rgba(242,203,54,1)",
        pointBorderColor: "rgba(242,203,54,1)",
        pointHoverBackgroundColor: "rgba(242,203,54,1)",
        pointHoverBorderColor: "rgba(242,203,54,1)",
        data: talentScores.value,
      },
      {
        label: t("app.positioning.average"),
        backgroundColor: !props.darkmode ? "rgba(29,29,29,0.2)" : "rgba(242,242,242,0.2)",
        borderColor: !props.darkmode ? "rgba(29,29,29,1)" : "rgba(242,242,242,1)",
        pointBackgroundColor: !props.darkmode ? "rgba(29,29,29,1)" : "rgba(242,242,242,1)",
        pointBorderColor: !props.darkmode ? "rgba(29,29,29,1)" : "rgba(242,242,242,1)",
        pointHoverBackgroundColor: !props.darkmode ? "rgba(29,29,29,1)" : "rgba(242,242,242,1)",
        pointHoverBorderColor: !props.darkmode ? "rgba(29,29,29,1)" : "rgba(242,242,242,1)",
        data: campaignAverageScore.value,
      },
    ],
  };
});
</script>
